/**
 * Grid/View displays the results of a search performed with a Grid. This view
 * loads the Grid, overwriting any existing filter set stored in the user/desk
 * session.
 */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { notify } from 'react-notify-toast';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { updateCastingGrid } from '@united-talent-agency/julius-frontend-store';
import SearchCastingsView from '../../../projects/projects-view/views/search-castings';
import GridName from '../../../../components/grid-name/grid-name';
import withStyles from '@material-ui/styles/withStyles';
import { EditGrid } from '../edit-grid';
import { getAlgoliaKeys } from '../../../../support/algolia/algolia-keys-service';


export class CastingGrid extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isAddOpen: false,
      notes: [],
      isNotesOpen: false,
      filterBadges: {},
      filterModel: props.filterModel,
      deskId: null,
      algoliaKeys: {},
    };
    this._isMounted = false;
    this.show = notify.createShowQueue();
    this.saveGrid = this.saveGrid.bind(this);
    this._onStorage = this._onStorage.bind(this);
  }
  async refreshAlgoliaKeys() {
    try {
      const keys = await getAlgoliaKeys();
      // Check if component is still mounted before setting state
      if (!this._isMounted) return;
      if (keys && keys.appId && keys.searchApiKey) {
        this.setState({ algoliaKeys: keys });
      } else {
        console.warn('Received invalid Algolia keys. Keeping previous keys.');
        // Ensure algoliaKeys is set to empty object if keys are invalid
        if (!this.state.algoliaKeys || !this.state.algoliaKeys.appId) {
          this.setState({ algoliaKeys: {} });
        }
      }
    } catch (error) {
      console.error('Error fetching Algolia keys:', error);
      // Check if component is still mounted before setting state
      if (!this._isMounted) return;
      // Set algoliaKeys to empty object on error so component can still render
      // Child components should handle empty/invalid keys gracefully
      this.setState({ algoliaKeys: {} });
    }
  }

  _onStorage(e) {
    // Listen for Algolia keys updates from other tabs (storage events only fire for cross-tab changes)
    if (e.key === 'algoliaKeys' && this._isMounted) {
      this.refreshAlgoliaKeys();
    }
  }

  _checkForKeyUpdates() {
    // Check if keys in localStorage have changed (for same-tab updates)
    // Storage events don't fire for same-tab changes, so we need to poll
    if (!this._isMounted) return;
    
    try {
      const storedKeysStr = localStorage.getItem('algoliaKeys');
      if (!storedKeysStr) return;
      
      const storedKeys = JSON.parse(storedKeysStr);
      const currentKeys = this.state.algoliaKeys;
      
      // Compare keys to detect changes (check appId and searchApiKey)
      if (storedKeys?.appId && storedKeys?.searchApiKey) {
        if (!currentKeys?.appId || 
            currentKeys.appId !== storedKeys.appId || 
            currentKeys.searchApiKey !== storedKeys.searchApiKey) {
          // Keys have changed, refresh component state
          this.refreshAlgoliaKeys();
        }
      }
    } catch (error) {
      // Ignore JSON parse errors or other localStorage issues
    }
  }

  componentDidMount(){
    this._isMounted = true;
    // Centralized Algolia keys service handles refresh scheduling + retries.
    // Call without await to avoid blocking React's lifecycle
    this.refreshAlgoliaKeys();
    // Listen for storage events to detect when keys are refreshed by other tabs
    window.addEventListener('storage', this._onStorage);
    // Check for same-tab key updates periodically (every 30 seconds)
    // This ensures components stay in sync with manager's proactive refreshes
    this._keyCheckInterval = setInterval(() => this._checkForKeyUpdates(), 30 * 1000);
  }

  componentWillUnmount() {
    this._isMounted = false;
    window.removeEventListener('storage', this._onStorage);
    if (this._keyCheckInterval) {
      clearInterval(this._keyCheckInterval);
    }
  }


  render() {
    const { grid, filterModel, classes, user, desk } = this.props;
    const canEdit = grid.deskId === desk._id;

    return (
      <React.Fragment>
        <Helmet>
          <title>{grid && grid.name}</title>
        </Helmet>
        <div>
          <div style={{ marginLeft: 'auto', marginRight: 0 }}>
            <GridName
              canEdit={canEdit}
              creatingDesk={grid.deskId}
              archived={grid.archived || false}
              name={grid.name}
              saveChanges={async name => {
                grid.name = name;
                await this.saveGrid(grid);
              }}
            />
          </div>
          {canEdit && <EditGrid saveGrid={this.saveGrid} classes={classes} grid={grid} show={this.show} />}
        </div>
        {this.state.algoliaKeys && <SearchCastingsView
          updateGrid={async grid => await this.saveGrid(grid)}
          canEdit={canEdit}
          user={user}
          desk={desk}
          filterModel={filterModel}
          grid={grid}
          algoliaKeys={this.state.algoliaKeys}
        />
        }
      </React.Fragment>
    );
  }

  // eslint-disable-next-line no-undef
  saveGrid = async grid => {
    this.setState({ isSaving: true });
    const { desk } = this.props;
    const originalGrid = this.props.grid || {};
    const currentDeskId = desk._id;
    if (originalGrid.deskId === currentDeskId) {
      await this.props.updateCastingGrid(grid._id, currentDeskId, grid);
      this.show(`${grid.name} updated`, 'custom', 1000, {
        background: '#000000',
        text: '#FFFFFF',
      });
    }
    this.setState({ isSaving: false });
    return grid;
  };
}

const styles = {
  button: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    border: 'none',
    fontSize: 14,
    lineHeight: 1,
    marginLeft: 10,
    backgroundColor: 'unset',
    padding: '5px 0',
    textTransform: 'uppercase',
  },
  icon: {
    marginRight: 10,
  },
  rightAlign: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: 15,
  },
};

export default withRouter(connect(null, { updateCastingGrid })(withStyles(styles)(CastingGrid)));
